import api from './api';
import { variableCapALAPI, variableDesDeLAPI,
         relacioCapALAPI, relacioDesDeLAPI } from './mapesBuclesCausals';
import { capALAPI, desDeLAPI, correspondenciaIdentificadors } from './mapesEstocsFluxos';
import { capALAPI as ssmCapALAPI, desDeLAPI as ssmDesDeLAPI } from './mapesSSM';

// La traducció entre el que dibuixa el llenç i el que desa la base viu a
// `mapesEstocsFluxos.js`, sense cap import i amb proves pròpies
// (`npm run prova:estocs`). Abans eren dues funcions escrites aquí mateix, i
// des que les fletxes apunten als seus extrems per identificador ja no n'hi ha
// prou amb traduir camp a camp: cal refer la correspondència d'identificadors.

const modelService = {
  getCausalLoop: async (modelId) => {
    const { data } = await api.get(`/models/${modelId}/causal-loop`);
    return {
      variables: (data.variables || []).map(variableDesDeLAPI),
      relationships: (data.relationships || []).map(relacioDesDeLAPI),
    };
  },

  // `basatEn` és LA DATA QUE TENÍEM QUAN VAM CARREGAR EL MODEL, i el servidor contesta 409 si
  // la seva és més nova: vol dir que algú ha desat entremig i escriure-hi a sobre esborraria la
  // seva feina sense que ningú se n'assabentés. Vegeu `desaDamuntDeQue` a `model.routes.js`.
  //
  // I la `versio` que torna s'ha de guardar, o el segon desat de la mateixa persona xocaria amb
  // ella mateixa.
  saveCausalLoop: async (modelId, variables, relationships, basatEn) => {
    const { data } = await api.put(`/models/${modelId}/causal-loop`, {
      basatEn,
      variables: (variables || []).map(variableCapALAPI),
      relationships: (relationships || []).map(relacioCapALAPI),
    });
    return {
      versio: data.versio,
      variables: (data.variables || []).map(variableDesDeLAPI),
      relationships: (data.relationships || []).map(relacioDesDeLAPI),
    };
  },

  getModels: async (params = {}) => {
    const response = await api.get('/models', { params });
    return response.data;
  },

  getModel: async (id) => {
    const response = await api.get(`/models/${id}`);
    return response.data;
  },

  createModel: async (data) => {
    const response = await api.post('/models', data);
    return response.data;
  },

  updateModel: async (id, data) => {
    const response = await api.put(`/models/${id}`, data);
    return response.data;
  },

  deleteModel: async (id) => {
    const response = await api.delete(`/models/${id}`);
    return response.data;
  },

  // Arxivar: apartar-lo de la llista sense perdre'l. És el que es proposa primer, perquè fa la
  // mateixa feina que esborrar en gairebé tots els casos i no és irreversible.
  arxiva: async (id, arxivat) => (await api.put(`/models/${id}/arxiu`, { arxivat })).data,

  // A qui està assignat el model. Només un instructor amb els seus alumnes, o l'admin.
  assigna: async (id, usuaris) => (await api.put(`/models/${id}/comparticio`, { usuaris })).data,

  // Les persones a qui es pot assignar: els alumnes de qui ho demana.
  elsMeusAlumnes: async () => (await api.get('/users/alumnes')).data.users || [],

  getComponents: async (modelId) => {
    const response = await api.get(`/models/${modelId}/components`);
    return desDeLAPI(response.data.components || []);
  },

  // Desa tot el dibuix de cop; el servidor substitueix el que hi havia.
  // Retorna els components amb els identificadors definitius, que els assigna
  // la base de dades: els que fa servir el canvas mentre dibuixes ("1", "2"…)
  // són provisionals i no sobreviuen al desat.
  saveComponents: async (modelId, components, basatEn) => {
    const enviats = capALAPI(components);
    const response = await api.put(`/models/${modelId}/components`, { basatEn, components: enviats });
    const tornats = response.data.components || [];
    // ELS EXTREMS DE LES FLETXES S'HAN DE REFER.
    //
    // Mentre es dibuixa, una canonada apunta als identificadors provisionals del navegador
    // (`stock_1724…`). En desar, la base n'assigna d'UUID i els torna. Sense refer-los, després
    // del primer desat cap fletxa trobaria els seus extrems i el dibuix es desmuntaria — que és
    // exactament el que passava amb les relacions causals fins al 26/08/2026.
    return { components: desDeLAPI(tornats, correspondenciaIdentificadors(enviats, tornats)),
             versio: response.data.versio };
  },

  getSoftSystems: async (modelId) => {
    const { data } = await api.get(`/models/${modelId}/soft-systems`);
    return ssmDesDeLAPI(data);
  },

  // Desa NOMÉS les etapes que s'enviïn. Vegeu `mapesSSM.js`: aquí es treballa etapa per etapa i
  // durant una sessió llarga, i un «substitueix-ho tot» seria massa fàcil de disparar sense voler.
  saveSoftSystems: async (modelId, dades, basatEn) => {
    const { data } = await api.put(`/models/${modelId}/soft-systems`,
      { ...ssmCapALAPI(dades), basatEn });
    return { ...ssmDesDeLAPI(data), versio: data.versio };
  },
};

export default modelService;